import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, User, MapPin, Warning, X, Clock,
  PauseCircle, PlayCircle, ArrowsClockwise, Archive, CheckCircle, Users, CalendarBlank, MagnifyingGlass,
  Calendar, Check, CaretDown, CaretUp, CaretRight, StackSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DemoBanner, HelpTip } from "@/components/DemoHelpers";
import OperatorPageTip from "@/components/OperatorPageTip";
import { hasExpiredUnpaidFee } from "@/lib/feeExpiry";
import { PracticeReminderBell } from "@/components/PracticeReminderBell";

const ROLE_COLORS = {
  desk_operator: "#4F46E5",
  site_operator: "#166534",
  tech_operator: "#CA8A04",
  accounting_operator: "#6D28D9",
  admin: "#991B1B",
  professional: "#0369A1",
};

// Definizioni colonne (chiave -> {titolo, icon, colore})
const OP_COLUMNS = [
  { key: "altro_operatore", title: "In coda", icon: Users, tone: "slate" },
  { key: "assegnazione", title: "Assegnazione", icon: ArrowRight, tone: "purple" },
  { key: "in_lavorazione", title: "In lavorazione", icon: PlayCircle, tone: "emerald" },
  { key: "in_stand_by", title: "In stand by", icon: PauseCircle, tone: "amber" },
  { key: "completata", title: "Completata (60 gg)", icon: CheckCircle, tone: "sky" },
];
// Iter105/207.19: Admin/Supervisor vede in più "In attesa di pagamento"
// (unificata: raccoglie sia pratiche condivise non pagate sia split saldo
// pendente) come prima colonna, e la colonna "Da riassegnare". Gli operatori
// non vedono la colonna pagamenti.
const ADMIN_COLUMNS = [
  { key: "attesa_pagamento_cliente", title: "In attesa di pagamento", icon: Clock, tone: "orange" },
  { key: "da_riassegnare", title: "Da riassegnare", icon: Warning, tone: "red" },
  ...OP_COLUMNS.filter(c => c.key !== "altro_operatore" && c.key !== "assegnazione"),
];
const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  red: "bg-red-100 text-red-700 border-red-200",
  orange: "bg-orange-100 text-orange-700 border-orange-200",
};


/**
 * VisitGroupCard (Iter75) — Card "fascio" che raggruppa N pratiche dello stesso
 * `visit_group_id` (pacchetto chained con sopralluogo condiviso).
 * Mostra un header compatto con conteggio + slot + indirizzo; espansione
 * on-click rende le singole KanbanCard interne.
 */
function VisitGroupCard({ cards, column, isAdmin, isProfessional, meId, notifCounts, onRefresh, onMarkRead }) {
  const [expanded, setExpanded] = useState(false);
  // Primary card = prima con visit_group_role="primary" o fallback alla prima.
  const primary = cards.find(c => c.visit_group_role === "primary") || cards[0];
  const totalUnread = cards.reduce((sum, c) => sum + (notifCounts[c._id] || 0), 0);
  const fmtSlot = (iso) => {
    if (!iso) return null;
    try { return new Date(String(iso).replace(" ", "T")).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return null; }
  };
  const slot = fmtSlot(primary?.scheduled_appointment);

  return (
    <div
      className="rounded-lg border-2 border-indigo-300 bg-indigo-50/50 shadow-sm"
      data-testid={`visit-group-${primary?.visit_group_id || primary?._id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-indigo-100/50 rounded-t-lg transition"
        aria-expanded={expanded}
        data-testid={`visit-group-toggle-${primary?.visit_group_id || primary?._id}`}
      >
        <div className="mt-0.5 shrink-0 w-7 h-7 rounded-md bg-indigo-600 flex items-center justify-center text-white">
          <StackSimple size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-800">
              Fascio sopralluogo
            </span>
            <span className="text-[10px] font-mono bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">
              {cards.length} pratiche
            </span>
            {totalUnread > 0 && (
              <span className="text-[10px] font-mono bg-red-600 text-white px-1.5 py-0.5 rounded-full" data-testid="visit-group-unread">
                {totalUnread} nuove
              </span>
            )}
          </div>
          <div className="text-xs text-slate-700 mt-1 truncate">
            <MapPin size={11} weight="bold" className="inline mr-1 text-slate-500" />
            {primary?.property_address || "Indirizzo non disponibile"}
          </div>
          {slot && (
            <div className="text-xs text-indigo-800 font-medium mt-0.5">
              <CalendarBlank size={11} weight="bold" className="inline mr-1" />
              {slot}
              <span className="text-slate-500 font-normal ml-1">· un solo appuntamento</span>
            </div>
          )}
          <div className="text-[11px] text-slate-600 mt-1 truncate">
            {cards.map(c => c.service_name).filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="shrink-0 text-indigo-700">
          {expanded ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
        </div>
      </button>
      {expanded && (
        <div className="p-2 pt-0 space-y-2 border-t border-indigo-200" data-testid={`visit-group-expanded-${primary?.visit_group_id || primary?._id}`}>
          {cards.map(c => (
            <KanbanCard key={c._id} card={c} column={column}
              isAdmin={isAdmin} isProfessional={isProfessional}
              meId={meId} onRefresh={onRefresh}
              unreadCount={notifCounts[c._id] || 0}
              onMarkRead={onMarkRead} />
          ))}
        </div>
      )}
    </div>
  );
}


function KanbanCard({ card, column, isAdmin, isProfessional, meId, onRefresh, unreadCount = 0, onMarkRead }) {
  const nav = useNavigate();

  // Iter208.85 — Il prezzo è stato rimosso dalle card kanban per tutti gli utenti.

  // Mini progress bar: indice dello step corrente + indice del mio step (se sono operatore in coda)
  const steps = Array.isArray(card.wf_steps) ? card.wf_steps : [];
  const currentIdx = steps.findIndex(s => s.id === card.current_step_id);
  const myStepIdx = (() => {
    if (isAdmin || isProfessional || !meId) return -1;
    const sa = card.step_assignments || {};
    for (let i = 0; i < steps.length; i++) {
      if (sa[steps[i].id] === meId) return i;
    }
    return -1;
  })();

  const currentStep = steps.find(s => s.id === card.current_step_id);
  const needsForm = !!(currentStep && currentStep.has_form && currentStep.form_schema);
  const formCompleted = !!(card.submitted_forms && card.submitted_forms[card.current_step_id]?.completed_at);


  const daysLeft = ["completed", "cancelled"].includes(card.status) ? null : card.days_left;
  const urgency = daysLeft === null || daysLeft === undefined ? "none"
    : daysLeft < 0 ? "overdue" : daysLeft === 0 ? "today" : daysLeft <= 2 ? "soon" : "ok";
  const urgencyTitle = {
    overdue: `Consegna in ritardo di ${Math.abs(daysLeft)} gg`,
    today: "Consegna prevista oggi",
    soon: `Consegna tra ${daysLeft} gg`,
    ok: `Consegna tra ${daysLeft} gg`,
  }[urgency];

  return (
    <div
      className={`kanban-card fade-up cursor-pointer bg-white ${card.status === "assigned" ? "border-yellow-400 border-2" : "border"} rounded-lg p-2 hover:shadow-md transition-shadow min-w-0 max-w-full overflow-visible relative`}
      onClick={() => nav(`/practices/${card.parent_practice_id || card._id}`)}
      data-testid={`practice-card-${card._id}`}
      data-assigned={card.status === "assigned" ? "true" : undefined}
      data-urgency={urgency}
      title={urgencyTitle}
    >
      {isAdmin && card.no_slot_alert && (
        <div
          className="w-full mb-2 flex items-center gap-2 rounded-md bg-red-50 border border-red-300 text-red-800 px-2 py-1.5 text-[11px] font-medium animate-pulse"
          data-testid={`no-slot-alert-${card._id}`}
          title="Apri la pratica per riassegnare o riselezionare gli slot"
        >
          <Warning size={13} weight="fill" className="shrink-0" />
          <span className="truncate text-left">Nessuna disponibilità operatore (7gg)</span>
        </div>
      )}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="label-eyebrow" style={{ color: ROLE_COLORS[card.current_assigned_role] || "#52525B" }}>
          {card.priority}
          {card.no_commission && !card.archived_without_delivery && <span className="ml-1 px-1 text-[9px] bg-amber-100 text-amber-700 rounded" data-testid={`badge-revived-${card._id}`}>RIANIMATA</span>}
          {card.archived_without_delivery && <span className="ml-1 px-1 text-[9px] bg-slate-200 text-slate-700 rounded" data-testid={`badge-archived-${card._id}`}>ARCHIVIATA</span>}
        </div>
        {/* Iter208.85 — Badge notifiche non lette spostato nell'header (era
            in absolute top-right e veniva tagliato). Il prezzo €X è stato
            rimosso da tutte le card come richiesto. */}
        {!isProfessional && (
          <div className="flex items-center gap-1 ml-auto shrink-0">
            <PracticeReminderBell practiceId={card._id} reminder={card.my_reminder} />
          </div>
        )}
        {unreadCount > 0 && (
          <span
            className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white shadow-sm shadow-red-500/30 shrink-0"
            data-testid={`unread-badge-${card._id}`}
            title={`${unreadCount} notific${unreadCount === 1 ? "a" : "he"} non lett${unreadCount === 1 ? "a" : "e"}`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>
      <h4 className="font-medium text-sm mb-2 leading-snug truncate">{card.service_name}</h4>
      {hasExpiredUnpaidFee(card) && (
        <div
          className="mb-2 inline-flex items-center gap-1 rounded-md bg-red-50 border border-red-300 px-2 py-0.5 text-[10px] font-semibold text-red-800"
          data-testid={`fee-expired-badge-${card._id}`}
          title="Bollettino non pagato da oltre 7 giorni: pratica a rischio annullamento"
        >
          <Warning size={11} weight="fill" className="shrink-0" /> Annullamento per mancato pagamento
        </div>
      )}
      {/* Iter114 — Badge stato firma documento */}
      {card.signature_status === "pending" && (
        <div
          className="mb-2 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
          data-testid={`sig-badge-pending-${card._id}`}
          title="Documento inviato in firma, in attesa del PDF firmato"
        >
          <Clock size={11} weight="fill" className="shrink-0" /> In attesa firma
        </div>
      )}
      {card.signature_status === "signed" && (
        <div
          className="mb-2 inline-flex items-center gap-1 rounded-md bg-green-50 border border-green-200 px-2 py-0.5 text-[10px] font-semibold text-green-800"
          data-testid={`sig-badge-signed-${card._id}`}
          title="Documento firmato ricevuto"
        >
          <CheckCircle size={11} weight="fill" className="shrink-0" /> Firmato
        </div>
      )}
      {/* 📅 Sopralluogo fissato: badge ben visibile per tutti gli operatori */}
      {card.scheduled_appointment && (
        <div
          className="mb-2 flex items-center gap-1.5 rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-[11px] font-semibold text-blue-800"
          data-testid={`scheduled-appointment-${card._id}`}
          title="Data e ora del sopralluogo"
        >
          <Calendar size={12} weight="fill" className="shrink-0" />
          <span className="truncate">
            {new Date(String(card.scheduled_appointment).replace(" ", "T")).toLocaleString("it-IT", {
              day: "2-digit", month: "2-digit", year: "2-digit",
              hour: "2-digit", minute: "2-digit",
            })}
          </span>
        </div>
      )}
      {card.property_address && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2 min-w-0">
          <MapPin size={11} className="shrink-0" />{" "}
          <span className="truncate" data-testid={`practice-address-${card._id}`}>
            {card.property_address}{card.property_city ? `, ${card.property_city}` : ""}
          </span>
        </div>
      )}
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3 min-w-0">
        <User size={11} className="shrink-0" /> <span className="truncate" data-testid={`practice-pro-${card._id}`}>{card.professional_company_name || card.professional_name}</span>
      </div>
      {needsForm && (
        <div className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest rounded px-1.5 py-0.5 mb-2 ${formCompleted ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}
          data-testid={`form-badge-${card._id}`}>
          📋 {formCompleted ? "Form compilato" : "Form da compilare"}
        </div>
      )}

      {steps.length > 0 && card.status !== "completed" && (
        <div className="mb-3" data-testid={`wf-progress-${card._id}`}>
          <div className="flex items-center gap-1">
            {steps.map((s, i) => {
              const done = currentIdx >= 0 && i < currentIdx;
              const active = i === currentIdx;
              const mine = i === myStepIdx;
              return (
                <div key={s.id} className="flex-1 flex items-center gap-1" title={`${s.order}. ${s.name}${mine ? " (tocca a te)" : ""}`}>
                  <div className={`flex-1 h-1.5 rounded-full transition-colors ${
                    done ? "bg-emerald-500" :
                    active ? "bg-purple-600" :
                    mine ? "bg-purple-300 ring-2 ring-purple-400 ring-offset-1" :
                    "bg-slate-200"
                  }`} />
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
            <span className="truncate">
              {currentIdx >= 0
                ? <>Step {currentIdx + 1}/{steps.length} · <strong className="text-slate-700">{steps[currentIdx].name}</strong></>
                : <>{steps.length} step</>
              }
            </span>
            {myStepIdx > currentIdx && myStepIdx >= 0 && (
              <span className="text-purple-700 font-semibold">Tocca a te allo step {myStepIdx + 1}</span>
            )}
          </div>
        </div>
      )}

      {column === "altro_operatore" && (
        <div className="text-[11px] px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-600">
          Attualmente su: <strong>{card.current_step_name || "—"}</strong>
        </div>
      )}

    </div>
  );
}

function ArchiveDialog({ open, onClose }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ops, setOps] = useState([]);
  const [chosenOp, setChosenOp] = useState("");
  const [tab, setTab] = useState("completed"); // Iter208.12 — "completed" | "cancelled"

  useEffect(() => {
    if (!open) return;
    api.get("/practices/archive").then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, [open]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/practices/${selected._id}/available-operators`).then(r => setOps(r.data || [])).catch(() => setOps([]));
  }, [selected]);

  const revive = async () => {
    if (!selected || !chosenOp) return;
    try {
      await api.post(`/practices/${selected._id}/revive`, { operator_id: chosenOp });
      toast.success("Pratica rianimata e riassegnata (senza commissione)");
      setSelected(null); setChosenOp("");
      const r = await api.get("/practices/archive");
      setItems(r.data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  // Iter208.12 — Suddivisione in tab Completate / Annullate
  const completed = useMemo(() => (items || []).filter(p => p.status === "completed"), [items]);
  const cancelled = useMemo(() => (items || []).filter(p => p.status === "cancelled"), [items]);
  const visibleItems = tab === "cancelled" ? cancelled : completed;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setSelected(null); setChosenOp(""); } }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive size={20} weight="duotone" /> Archivio pratiche (oltre 60 giorni)</DialogTitle>
          <DialogDescription>Rianima una pratica riassegnandola ad un operatore. L'operazione non genera commissione.</DialogDescription>
        </DialogHeader>

        {!selected && (
          <div className="flex items-center gap-2 border-b pb-2" data-testid="archive-tabs">
            <button
              type="button"
              onClick={() => setTab("completed")}
              className={
                "px-3 py-1.5 text-sm font-semibold rounded-t-md " +
                (tab === "completed"
                  ? "bg-purple-100 text-purple-800 border border-purple-300 border-b-0"
                  : "text-slate-600 hover:bg-slate-50")
              }
              data-testid="archive-tab-completed"
              aria-pressed={tab === "completed"}
            >
              Completate <span className="ml-1 text-[10px] font-bold bg-slate-200 text-slate-700 rounded-full px-1.5 py-0.5">{completed.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setTab("cancelled")}
              className={
                "px-3 py-1.5 text-sm font-semibold rounded-t-md " +
                (tab === "cancelled"
                  ? "bg-red-100 text-red-800 border border-red-300 border-b-0"
                  : "text-slate-600 hover:bg-slate-50")
              }
              data-testid="archive-tab-cancelled"
              aria-pressed={tab === "cancelled"}
            >
              Annullate <span className="ml-1 text-[10px] font-bold bg-red-200 text-red-800 rounded-full px-1.5 py-0.5">{cancelled.length}</span>
            </button>
          </div>
        )}

        {!selected ? (
          visibleItems.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid={`archive-empty-${tab}`}>
              {tab === "cancelled" ? "Nessuna pratica annullata." : "Nessuna pratica archiviata."}
            </div>
          ) : (
            <div className="space-y-1" data-testid={`archive-list-${tab}`}>
              {visibleItems.map(p => {
                const isCancelled = p.status === "cancelled";
                const dateIso = isCancelled ? (p.cancelled_at || p.updated_at) : p.completed_at;
                return (
                  <div
                    key={p._id}
                    className={
                      "flex items-center justify-between p-3 border rounded transition-colors " +
                      (isCancelled ? "border-red-200 hover:border-red-400 bg-red-50/40" : "hover:border-purple-400")
                    }
                    data-testid={`archive-row-${p._id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm truncate">{p.service_name}</div>
                        {isCancelled && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-600 text-white shadow-sm"
                            data-testid={`archive-badge-cancelled-${p._id}`}
                          >
                            <X size={10} weight="bold" /> ANNULLATA
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{p.property_address || "—"} · {p.professional_company_name || p.professional_name}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {isCancelled ? "Annullata il " : "Completata il "}
                        {dateIso ? new Date(dateIso).toLocaleDateString("it-IT") : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isCancelled ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { onClose(); nav(`/practices/${p._id}`); }}
                          data-testid={`archive-open-cancelled-${p._id}`}
                        >
                          Vedi dettaglio
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setSelected(p)} data-testid={`archive-revive-${p._id}`}>
                          <ArrowsClockwise size={13} className="mr-1" /> Rianima
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="space-y-3">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded text-sm">
              <div className="font-semibold">{selected.service_name}</div>
              <div className="text-xs text-muted-foreground">{selected.property_address}</div>
            </div>
            <div>
              <label className="text-sm font-medium">Assegna a</label>
              <select value={chosenOp} onChange={e => setChosenOp(e.target.value)}
                className="w-full h-10 border rounded-md px-3 text-sm mt-1"
                data-testid="revive-operator-select">
                <option value="">— Scegli operatore —</option>
                {ops.map(o => (
                  <option key={o.operator_id} value={o.operator_id}>
                    {o.first_name} {o.last_name} · {o.operator_type?.toUpperCase()} · score {o.score}/5
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setSelected(null); setChosenOp(""); }}>Indietro</Button>
              <Button className="ht-gradient border-0" disabled={!chosenOp} onClick={revive} data-testid="revive-confirm-btn">
                Rianima e assegna
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function KanbanBoard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [board, setBoard] = useState(null);
  const [notifCounts, setNotifCounts] = useState({}); // { practice_id: unread_count }
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchParams] = useSearchParams();
  const focusCol = searchParams.get("filter");

  const isAdmin = user.role === "admin" || user.is_supervisor;
  const isProfessional = user.role === "professional";
  const isOperator = !isAdmin && !isProfessional;

  // Iter207.10 — Layout mobile: colonne collassabili (sotto md=768px).
  // `openCols` mappa la colonna aperta al momento; su desktop viene ignorato.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const [openCols, setOpenCols] = useState({});
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", onChange); } catch { mq.addListener(onChange); }
    return () => {
      try { mq.removeEventListener("change", onChange); } catch { mq.removeListener(onChange); }
    };
  }, []);

  // 🔒 Bacheca operatori: i professionisti NON possono accedervi.
  // Redirect automatico verso Marketplace/Pratiche.
  useEffect(() => {
    if (isProfessional) {
      nav("/marketplace?tab=recap", { replace: true });
    }
  }, [isProfessional, nav]);

  const cols = isOperator ? OP_COLUMNS : ADMIN_COLUMNS;

  // Iter207.10 — Auto-espansione mobile della colonna con più pratiche.
  // Si attiva solo la prima volta che `board` viene caricato (o quando l'utente
  // ridimensiona diventando mobile). Non sovrascrive scelte esplicite dell'utente.
  const [autoOpenedForBoard, setAutoOpenedForBoard] = useState(false);
  useEffect(() => {
    if (!isMobile || !board?.columns || autoOpenedForBoard) return;
    let maxKey = null;
    let maxLen = -1;
    for (const c of cols) {
      const len = (board.columns[c.key] || []).length;
      if (len > maxLen) { maxLen = len; maxKey = c.key; }
    }
    if (maxKey && maxLen > 0) {
      setOpenCols((prev) => ({ ...prev, [maxKey]: true }));
    }
    setAutoOpenedForBoard(true);
  }, [isMobile, board, cols, autoOpenedForBoard]);

  const load = () => api.get("/practices/personal-board").then(r => setBoard(r.data));
  const loadNotifCounts = () =>
    api.get("/notifications/per-practice")
      .then(r => setNotifCounts(r.data?.per_practice || {}))
      .catch(() => setNotifCounts({}));
  const markPracticeRead = async (practiceId) => {
    // Ottimistica: aggiorna lo state locale subito, poi conferma col server.
    setNotifCounts(prev => {
      const next = { ...prev };
      delete next[practiceId];
      return next;
    });
    try {
      await api.post("/notifications/mark-read", { practice_id: practiceId });
    } catch (err) {
      // In caso di errore, ricarica dallo stato reale del server.
      loadNotifCounts();
    }
  };
  useEffect(() => {
    if (isProfessional) return; // no fetch per professionisti
    load();
    loadNotifCounts();
  }, [isProfessional]);
  useEffect(() => {
    if (isProfessional) return;
    const t = setInterval(load, 30000);
    const tn = setInterval(loadNotifCounts, 30000);
    return () => { clearInterval(t); clearInterval(tn); };
  }, [isProfessional]);

  // Scroll alla colonna richiesta via query param (?filter=in_lavorazione)
  useEffect(() => {
    if (!board || !focusCol) return;
    const el = document.querySelector(`[data-testid="column-${focusCol}"]`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" }), 150);
    }
  }, [board, focusCol]);

  if (!board) return <div>Caricamento...</div>;

  const q = query.trim().toLowerCase();
  const filterCard = (c) => {
    if (!q) return true;
    return [c.service_name, c.property_address, c.professional_company_name || c.professional_name]
      .filter(Boolean).some(f => String(f).toLowerCase().includes(q));
  };

  return (
    <div>
      <OperatorPageTip
        pageKey="bacheca"
        title="La tua Bacheca pratiche"
        body="Ogni card è una pratica che ti è stata affidata. Trascinala tra le colonne o usa il pulsante Avanza per gestirla: Assegnazione → In lavorazione → Consegna. Usa Stand by se stai aspettando documenti."
      />
      <DemoBanner />
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Bacheca personale</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
            Le mie pratiche
            <HelpTip text="Le pratiche entrano in Assegnazione quando ti vengono affidate. Premi Avanza per accettare e iniziare a lavorarle. Puoi metterle in Stand by in attesa di documenti o revocare l'assegnazione." />
          </h1>
          <p className="text-muted-foreground mt-2">
            {isAdmin ? "Vista globale: unione delle bacheche di tutti gli operatori." :
             isProfessional ? "Le tue pratiche in corso, divise per stato di lavorazione." :
             "Ogni pratica scorre attraverso i tuoi 5 stati personali."}
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setArchiveOpen(true)} data-testid="open-archive-btn">
            <Archive size={16} className="mr-2" /> Archivio pratiche
          </Button>
        )}
      </div>

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per servizio, indirizzo, professionista…"
          className="pl-9 pr-9 h-10"
          data-testid="kanban-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-muted-foreground"
            data-testid="kanban-search-clear"
            aria-label="Pulisci ricerca"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className={`grid gap-3 ${isOperator ? "grid-cols-1 md:grid-cols-3 lg:grid-cols-5" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"}`} data-testid="kanban-board">
        {cols.map(col => {
          const Icon = col.icon;
          const allItems = board.columns?.[col.key] || [];
          const items = allItems.filter(filterCard);
          const isFiltered = q && items.length !== allItems.length;
          // Iter207.10 — Su mobile la colonna è collassabile.
          const isOpen = !isMobile || !!openCols[col.key];
          const toggleCol = () => {
            if (!isMobile) return;
            setOpenCols((prev) => ({ ...prev, [col.key]: !prev[col.key] }));
          };
          return (
            <div key={col.key} className={`kanban-column bg-[color:hsl(var(--surface-alt))] rounded-lg p-2 min-w-0 ${isMobile ? "" : "min-h-[60vh]"}`} data-testid={`column-${col.key}`}>
              <button
                type="button"
                onClick={toggleCol}
                aria-expanded={isOpen}
                className={`w-full flex items-center justify-between px-2 py-2 mb-2 rounded border ${TONE_CLASSES[col.tone]} ${isMobile ? "cursor-pointer active:opacity-80" : "cursor-default"}`}
                data-testid={`column-header-${col.key}`}
              >
                <div className="flex items-center gap-1.5 font-semibold text-sm">
                  <Icon size={14} weight="duotone" /> {col.title}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-white/70 px-2 py-0.5 rounded-full" data-testid={`count-${col.key}`}>
                    {isFiltered ? `${items.length}/${allItems.length}` : items.length}
                  </span>
                  {isMobile && (
                    <CaretRight
                      size={14}
                      weight="bold"
                      className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  )}
                </div>
              </button>
              {isOpen && (
              <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: isMobile ? "none" : "65vh" }}>
                {items.length === 0 ? (
                  <div className="text-center text-[11px] text-muted-foreground py-6">
                    {q ? "Nessun risultato" : "Nessuna pratica"}
                  </div>
                ) : (() => {
                  // Iter75: raggruppa per `visit_group_id` mantenendo l'ordine
                  // originale (chi appare prima nella lista guida il gruppo).
                  const groups = [];
                  const byGroup = new Map();
                  for (const c of items) {
                    const gid = c.visit_group_id;
                    if (!gid) {
                      groups.push({ type: "single", card: c });
                      continue;
                    }
                    if (!byGroup.has(gid)) {
                      const bucket = { type: "group", gid, cards: [] };
                      byGroup.set(gid, bucket);
                      groups.push(bucket);
                    }
                    byGroup.get(gid).cards.push(c);
                  }
                  return groups.map((g, gi) => {
                    if (g.type === "single" || (g.cards && g.cards.length < 2)) {
                      const c = g.type === "single" ? g.card : g.cards[0];
                      return (
                        <KanbanCard key={c._id} card={c} column={col.key}
                          isAdmin={isAdmin} isProfessional={isProfessional}
                          meId={user.id} onRefresh={load}
                          unreadCount={notifCounts[c._id] || 0}
                          onMarkRead={markPracticeRead} />
                      );
                    }
                    return (
                      <VisitGroupCard
                        key={g.gid || gi}
                        cards={g.cards}
                        column={col.key}
                        isAdmin={isAdmin}
                        isProfessional={isProfessional}
                        meId={user.id}
                        notifCounts={notifCounts}
                        onRefresh={load}
                        onMarkRead={markPracticeRead}
                      />
                    );
                  });
                })()}
              </div>
              )}
            </div>
          );
        })}
      </div>

      <ArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} />
    </div>
  );
}

